import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Zap, Truck, MapPin, Clock, User, Phone, Package, RefreshCw, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import DriverLiveMap from './DriverLiveMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DispatchPanel = ({ token, language, drivers, orders, rentals = [], onRefresh }) => {
  const [dispatches, setDispatches] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapDriverId, setMapDriverId] = useState(null);

  const fetchDispatches = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [dspRes, driversRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/dispatches`, { headers }),
        fetch(`${API_URL}/api/admin/available-drivers`, { headers })
      ]);
      if (dspRes.ok) setDispatches(await dspRes.json());
      if (driversRes.ok) setAvailableDrivers(await driversRes.json());
    } catch (err) {
      console.error('Error fetching dispatches:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDispatches();
    const t = setInterval(fetchDispatches, 5000);
    return () => clearInterval(t);
  }, [fetchDispatches]);

  const manualDispatch = async (orderId, driverId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dispatch/${orderId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ driver_id: driverId })
      });
      if (response.ok) {
        toast.success(language === 'pt' ? 'Entregador atribuido!' : 'Driver assigned!');
        fetchDispatches();
        if (onRefresh) onRefresh();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error');
      }
    } catch (err) {
      toast.error(language === 'pt' ? 'Erro ao atribuir' : 'Assignment failed');
    }
  };

  const unassignedOrders = orders.filter(o => !o.driver_id && o.status !== 'delivered' && o.status !== 'cancelled');
  const unassignedRentals = (rentals || []).filter(r => !r.driver_id && !['delivered', 'cancelled', 'pending_payment', 'expired'].includes(r.status));
  const unassigned = [
    ...unassignedOrders.map(o => ({ ...o, _dispatchId: o.order_id, _isRental: false })),
    ...unassignedRentals.map(r => ({
      ...r,
      _dispatchId: r.rental_id,
      _isRental: true,
      order_id: r.rental_id,
      customer_name: r.customer_name,
      delivery_location: r.delivery_location,
    })),
  ];

  const activeStatuses = ['delivered', 'cancelled', 'pending_payment', 'expired'];
  const assigned = [
    ...orders.filter(o => o.driver_id && !activeStatuses.includes(o.status)).map(o => ({ ...o, _dispatchId: o.order_id, _isRental: false })),
    ...(rentals || []).filter(r => r.driver_id && !activeStatuses.includes(r.status)).map(r => ({ ...r, _dispatchId: r.rental_id, _isRental: true })),
  ];
  const driverName = (id) => drivers.find(d => d.driver_id === id)?.name || id;

  const getDispatchStatusBadge = (status) => {
    switch (status) {
      case 'accepted': return <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle size={10} className="mr-1" />{language === 'pt' ? 'Aceite' : 'Accepted'}</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-700 text-xs"><XCircle size={10} className="mr-1" />{language === 'pt' ? 'Rejeitado' : 'Rejected'}</Badge>;
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-700 text-xs"><Clock size={10} className="mr-1" />{language === 'pt' ? 'Pendente' : 'Pending'}</Badge>;
      default: return <Badge className="bg-gray-100 text-gray-700 text-xs">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold text-[#004E64] flex items-center gap-2">
          <Zap className="text-[#FF6B35]" size={20} />
          {language === 'pt' ? 'Sistema de Despacho' : 'Dispatch System'}
        </h2>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => { fetchDispatches(); if (onRefresh) onRefresh(); }} data-testid="refresh-dispatch-btn">
          <RefreshCw size={14} className="mr-1" />
          {language === 'pt' ? 'Atualizar' : 'Refresh'}
        </Button>
      </div>

      {/* Available Drivers */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Truck className="text-[#2EC4B6]" size={16} />
            {language === 'pt' ? 'Entregadores Disponiveis' : 'Available Drivers'} ({availableDrivers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {availableDrivers.length === 0 ? (
            <div className="text-center py-6">
              <AlertTriangle size={32} className="mx-auto text-yellow-500 mb-2" />
              <p className="text-sm text-[#9FA2B4]">{language === 'pt' ? 'Nenhum entregador disponivel' : 'No drivers available'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {availableDrivers.map(d => (
                <button
                  key={d.driver_id}
                  onClick={() => setMapDriverId(d.driver_id)}
                  className="p-3 bg-[#F4F1EA] rounded-xl text-left w-full hover:ring-2 hover:ring-[#2EC4B6]/50 transition-shadow cursor-pointer"
                  data-testid={`available-driver-${d.driver_id}`}
                  title={language === 'pt' ? 'Ver no mapa em tempo real' : 'View live on map'}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {d.photo_url ? (
                      <img src={d.photo_url} alt={d.name} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 bg-[#2EC4B6] rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {d.name?.charAt(0) || 'D'}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold text-[#004E64]">{d.name}</p>
                      <p className="text-xs text-[#9FA2B4]">{d.vehicle_type}</p>
                    </div>
                  </div>
                  {d.latitude && d.longitude ? (
                    <p className="text-xs text-[#2EC4B6] flex items-center gap-1 mt-1 underline underline-offset-2">
                      <MapPin size={10} /> {language === 'pt' ? 'Ver no mapa (tempo real)' : 'View live on map'}
                    </p>
                  ) : (
                    <p className="text-xs text-[#9FA2B4] mt-1">{language === 'pt' ? 'Sem GPS' : 'No GPS'}</p>
                  )}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unassigned Orders */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Package className="text-[#FF6B35]" size={16} />
            {language === 'pt' ? 'Pedidos Sem Entregador' : 'Unassigned Orders'} ({unassigned.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {unassigned.length === 0 ? (
            <div className="text-center py-6">
              <CheckCircle size={32} className="mx-auto text-green-500 mb-2" />
              <p className="text-sm text-[#9FA2B4]">{language === 'pt' ? 'Todos os pedidos tem entregador' : 'All orders are assigned'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {unassigned.map(order => (
                <div key={order._dispatchId} className={`border rounded-xl p-3 ${order._isRental ? 'border-purple-300 bg-purple-50' : 'border-[#FF6B35]/20 bg-[#FF6B35]/5'}`} data-testid={`unassigned-order-${order._dispatchId}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#004E64] text-sm">#{order._dispatchId?.slice(-8)}</span>
                        {order._isRental && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">Shisha</Badge>
                        )}
                        <Badge className="bg-yellow-100 text-yellow-700 text-xs">{language === 'pt' ? 'Sem Entregador' : 'No Driver'}</Badge>
                      </div>
                      <p className="text-xs text-[#9FA2B4]">{order.customer_name} - {order.delivery_location}</p>
                      {order._isRental && (
                        <p className="text-xs text-purple-700">{order.shisha_name} · {order.flavor_name} · {order.duration_hours}h</p>
                      )}
                      <p className="text-xs font-bold text-[#FF6B35] mt-1">{'\u20AC'}{order.total?.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select onValueChange={(driverId) => manualDispatch(order._dispatchId, driverId)}>
                        <SelectTrigger className="w-[160px] h-8 text-xs rounded-xl" data-testid={`assign-driver-select-${order._dispatchId}`}>
                          <SelectValue placeholder={language === 'pt' ? 'Atribuir Entregador' : 'Assign Driver'} />
                        </SelectTrigger>
                        <SelectContent>
                          {drivers.filter(d => d.is_active).map(d => (
                            <SelectItem key={d.driver_id} value={d.driver_id} className="text-xs">
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* In-progress deliveries — swap driver */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Truck className="text-[#004E64]" size={16} />
            {language === 'pt' ? 'Em Curso — Trocar Entregador' : 'In Progress — Swap Driver'} ({assigned.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {assigned.length === 0 ? (
            <p className="text-sm text-[#9FA2B4] text-center py-4">{language === 'pt' ? 'Sem entregas em curso' : 'No deliveries in progress'}</p>
          ) : (
            <div className="space-y-3">
              {assigned.map(item => (
                <div key={item._dispatchId} className="border border-[#004E64]/10 rounded-xl p-3" data-testid={`assigned-order-${item._dispatchId}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#004E64] text-sm">#{item._dispatchId?.slice(-8)}</span>
                        {item._isRental && <Badge className="bg-purple-100 text-purple-700 text-xs">Shisha</Badge>}
                        <Badge className="bg-blue-100 text-blue-700 text-xs">{item.status}</Badge>
                      </div>
                      <p className="text-xs text-[#9FA2B4]">{item.customer_name} - {item.delivery_location}</p>
                      <p className="text-xs text-[#2EC4B6] mt-1 flex items-center gap-1">
                        <Truck size={10} /> {driverName(item.driver_id)}
                      </p>
                    </div>
                    <Select onValueChange={(driverId) => manualDispatch(item._dispatchId, driverId)}>
                      <SelectTrigger className="w-[160px] h-8 text-xs rounded-xl" data-testid={`swap-driver-select-${item._dispatchId}`}>
                        <SelectValue placeholder={language === 'pt' ? 'Trocar Entregador' : 'Swap Driver'} />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.filter(d => d.is_active && d.driver_id !== item.driver_id).map(d => (
                          <SelectItem key={d.driver_id} value={d.driver_id} className="text-xs">
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Dispatches */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="text-[#004E64]" size={16} />
            {language === 'pt' ? 'Historico de Despachos' : 'Dispatch History'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dispatches.length === 0 ? (
            <p className="text-sm text-[#9FA2B4] text-center py-4">{language === 'pt' ? 'Sem despachos' : 'No dispatches yet'}</p>
          ) : (
            <div className="space-y-2">
              {dispatches.slice(0, 20).map(d => (
                <div key={d.dispatch_id} className="flex items-center justify-between p-2 bg-[#F4F1EA] rounded-xl">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#004E64]">#{d.order_id?.slice(-8)}</span>
                      {getDispatchStatusBadge(d.status)}
                    </div>
                    <p className="text-xs text-[#9FA2B4]">
                      {d.driver_name} {d.distance_km ? `(${d.distance_km} km)` : ''}
                    </p>
                  </div>
                  <p className="text-xs text-[#9FA2B4]">
                    {d.created_at ? new Date(d.created_at).toLocaleString() : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live driver map dialog */}
      <DriverLiveMap
        token={token}
        driverId={mapDriverId}
        open={!!mapDriverId}
        onClose={() => setMapDriverId(null)}
        language={language}
      />
    </div>
  );
};

export default DispatchPanel;
